import { useEffect, useRef, useState } from 'react'
import { useTranslation } from '@spcc/core'
import { validateDriverId, validateSaudiPhone, normalizeSaudiPhoneForStorage, normalizeDriverIdForStorage } from '@spcc/core'
import { Button, Input, Label, Loader2, Check, UserCheck, Search, Plus, X, Truck, User as UserIcon, toast, ChevronDown, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SaudiPhoneInput } from '@spcc/uikit'
import type { TFunction } from 'i18next'
import type { Driver, Vehicle, VehicleType } from '../../domain/types'
import { DRIVER_PREFERRED_LANGUAGES } from '../../domain/types'
import { driversApi } from '../../infrastructure/api/drivers-api'
import { resourcesApi } from '../../infrastructure/api/resources-api'
import { vehiclesApi } from '../../infrastructure/api/vehicles-api'

type PlateFocusTarget = 'digits' | 'letters' | null

type ResourceOption = {
  id: string
  documentId?: string
  name: string
  driverName?: string
  powerUnitLabel?: string
  trailerLabel?: string
  raw: any
}

type ResourceManagementPanelProps = {
  requiredVehicleType?: VehicleType | null
  selectedResourceId?: string
  selectedResourceDocumentId?: string
  onSelectResource: (resource: any) => void
  onClearSelection?: () => void
  pageSize?: number
  searchPlaceholder?: string
  isGuestMode?: boolean  // Guest mode: create locally without API calls
  /** Tenant documentId to scope resource results to this tenant only (prevents cross-tenant data leakage) */
  tenantDocumentId?: string | null
}

const LICENSE_PLATE_DIGITS_LENGTH = 4
const LICENSE_PLATE_LETTERS_LENGTH = 3

function getAssetLabel(asset: any): string | undefined {
  const licensePlate = typeof asset?.licensePlate === 'string' ? asset.licensePlate.trim() : ''
  if (licensePlate) return licensePlate

  const plateNumber = typeof asset?.plateNumber === 'string' ? asset.plateNumber.trim() : ''
  if (plateNumber) return plateNumber

  const assetNumber = typeof asset?.assetNumber === 'string' ? asset.assetNumber.trim() : ''
  if (assetNumber) return assetNumber

  return undefined
}

function mapResourceToOption(resource: any): ResourceOption | null {
  const id = String(resource?.id ?? '').trim()
  const name = String(resource?.name ?? '').trim()
  if (!id || !name) return null

  const driverName =
    typeof resource?.driver?.name === 'string'
      ? resource.driver.name
      : (typeof resource?.driverName === 'string' ? resource.driverName : undefined)

  const powerUnitLabel = getAssetLabel(resource?.vehicle ?? resource?.powerUnit)
  const trailerLabel = getAssetLabel(resource?.trailer)

  return {
    id,
    documentId: resource?.documentId ?? undefined,
    name,
    driverName,
    powerUnitLabel,
    trailerLabel,
    raw: resource,
  }
}

function normalizePlateDigits(value: string): string {
  return value.replace(/\D/g, '').slice(0, LICENSE_PLATE_DIGITS_LENGTH)
}

function normalizePlateLetters(value: string): string {
  return value.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, LICENSE_PLATE_LETTERS_LENGTH)
}

function updateLicensePlateDigits(plateNumber: string, digits: string): string {
  const parts = plateNumber.split('-')
  const letters = parts[1] ?? ''
  return `${digits}${letters ? `-${letters}` : ''}`.trim()
}

function updateLicensePlateLetters(plateNumber: string, letters: string): string {
  const parts = plateNumber.split('-')
  const digits = parts[0] ?? ''
  return `${digits}${letters ? `-${letters}` : ''}`.trim()
}

function splitLicensePlate(plateNumber: string): { digits: string; letters: string } {
  const [digits, letters] = plateNumber.split('-')
  return {
    digits: digits ?? '',
    letters: letters ?? '',
  }
}

function getPlateDigitsUpdate(
  plateNumber: string,
  nextDigits: string
): { nextPlateNumber: string; focusTarget: PlateFocusTarget } {
  const normalizedDigits = normalizePlateDigits(nextDigits)
  return {
    nextPlateNumber: updateLicensePlateDigits(plateNumber, normalizedDigits),
    focusTarget: normalizedDigits.length === LICENSE_PLATE_DIGITS_LENGTH ? 'letters' : null,
  }
}

function getPlateLettersUpdate(plateNumber: string, nextLetters: string) {
  const normalizedLetters = normalizePlateLetters(nextLetters)
  return updateLicensePlateLetters(plateNumber, normalizedLetters)
}

function shouldFocusDigitsFromLetters(currentLetters: string, key: string): boolean {
  if (key !== 'Backspace') return false
  return normalizePlateLetters(currentLetters).length === 0
}

function applyPlateDigitsChange(
  plateNumber: string,
  nextValue: string,
  setPlateNumber: (next: string) => void,
  focusHandler: (target: PlateFocusTarget) => void
): void {
  const result = getPlateDigitsUpdate(plateNumber, nextValue)
  setPlateNumber(result.nextPlateNumber)
  focusHandler(result.focusTarget)
}

function applyPlateLettersChange(
  plateNumber: string,
  nextValue: string,
  setPlateNumber: (next: string) => void
): void {
  setPlateNumber(getPlateLettersUpdate(plateNumber, nextValue))
}

function applyPlateLettersKeyDown(
  currentLetters: string,
  key: string,
  focusHandler: (target: PlateFocusTarget) => void
): void {
  if (shouldFocusDigitsFromLetters(currentLetters, key)) {
    focusHandler('digits')
  }
}

function formatVehicleTypeLabel(
  vehicleType: VehicleType | null | undefined,
  t: TFunction
): string {
  if (vehicleType === 'mixer') return t('assets.mixer', { defaultValue: 'Mixer' })
  if (vehicleType === 'flatbed') return t('assets.flatbed', { defaultValue: 'Flatbed' })
  return t('assets.vehicleType', { defaultValue: 'Vehicle Type' })
}

export function ResourceManagementPanel({
  requiredVehicleType,
  selectedResourceId,
  selectedResourceDocumentId,
  onSelectResource,
  onClearSelection,
  pageSize = 10,
  searchPlaceholder,
  isGuestMode = false,  // Default to authenticated mode
  tenantDocumentId,
}: ResourceManagementPanelProps) {
  const { t } = useTranslation()
  const [resourceSearch, setResourceSearch] = useState('')
  const [debouncedResourceSearch, setDebouncedResourceSearch] = useState('')
  const [resourceOptions, setResourceOptions] = useState<ResourceOption[]>([])
  const [resourcePagination, setResourcePagination] = useState<any>(null)
  const [resourcePage, setResourcePage] = useState(1)
  const [resourceLoading, setResourceLoading] = useState(false)
  const [resourceLoadingMore, setResourceLoadingMore] = useState(false)
  const [showResourceForm, setShowResourceForm] = useState(isGuestMode)  // Guest mode defaults to create form
  const resourceFetchKeyRef = useRef(0)

  const [selectedDriver, setSelectedDriver] = useState<Driver | null>(null)
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(null)
  const [resourceName, setResourceName] = useState("")
  const [inlineDriverSearch, setInlineDriverSearch] = useState("")
  const [inlineVehicleSearch, setInlineVehicleSearch] = useState("")
  const [searchedDrivers, setSearchedDrivers] = useState<Driver[]>([])
  const [searchedVehicles, setSearchedVehicles] = useState<Vehicle[]>([])
  const [driverSearchLoading, setDriverSearchLoading] = useState(false)
  const [vehicleSearchLoading, setVehicleSearchLoading] = useState(false)
  const [driverSearchPagination, setDriverSearchPagination] = useState<any>(null)
  const [vehicleSearchPagination, setVehicleSearchPagination] = useState<any>(null)
  const [isCreatingResource, setIsCreatingResource] = useState(false)
  const [driverFormVisible, setDriverFormVisible] = useState(isGuestMode)  // Guest mode shows driver form by default
  const [vehicleFormVisible, setVehicleFormVisible] = useState(isGuestMode)  // Guest mode shows vehicle form by default
  const [newDriverData, setNewDriverData] = useState({ name: "", phone: "", whatsappNumber: "", driverId: "", preferredLanguage: "ar" })
  const [newVehicleData, setNewVehicleData] = useState({ plateNumber: "", capacity: "" })
  const [vehicleType, setVehicleType] = useState<VehicleType>(requiredVehicleType || 'mixer')
  const plateDigitsRef = useRef<HTMLInputElement>(null)
  const plateLettersRef = useRef<HTMLInputElement>(null)

  const plateParts = splitLicensePlate(newVehicleData.plateNumber)

  const focusPlateInput = (target: PlateFocusTarget) => {
    if (target === 'letters') plateLettersRef.current?.focus()
    if (target === 'digits') plateDigitsRef.current?.focus()
  }

  useEffect(() => {
    setVehicleType(requiredVehicleType || 'mixer')
  }, [requiredVehicleType])

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedResourceSearch(resourceSearch.trim())
    }, 250)
    return () => clearTimeout(timer)
  }, [resourceSearch])

  useEffect(() => {
    setResourcePage(1)
    setResourcePagination(null)
    setResourceOptions([])
  }, [debouncedResourceSearch])

  useEffect(() => {
    // Skip resource search for guest mode - they can't search existing resources
    if (isGuestMode) {
      setResourceLoading(false)
      setResourceLoadingMore(false)
      return
    }

    const searchTerm = debouncedResourceSearch.trim()
    const fetchKey = ++resourceFetchKeyRef.current
    if (resourcePage === 1) {
      setResourceLoading(true)
    } else {
      setResourceLoadingMore(true)
    }

    const filter: any = { status: 'active', page: resourcePage, pageSize }
    if (searchTerm) filter.search = searchTerm
    if (tenantDocumentId) filter.tenantDocumentId = tenantDocumentId
    if (requiredVehicleType) filter.powerUnitVehicleType = requiredVehicleType

    resourcesApi.getResourcesPaginated(filter)
      .then(({ data, pagination }) => {
        if (resourceFetchKeyRef.current !== fetchKey) return
        const mapped = (data || []).map(mapResourceToOption).filter(Boolean) as ResourceOption[]
        setResourceOptions(prev => resourcePage === 1 ? mapped : [...prev, ...mapped])
        setResourcePagination(pagination)
      })
      .catch(() => {
        if (resourceFetchKeyRef.current !== fetchKey) return
        setResourceOptions([])
        setResourcePagination(null)
      })
      .finally(() => {
        if (resourceFetchKeyRef.current !== fetchKey) return
        setResourceLoading(false)
        setResourceLoadingMore(false)
      })
  }, [debouncedResourceSearch, resourcePage, requiredVehicleType, pageSize, isGuestMode])

  const resourceHasMore = Boolean(resourcePagination && resourcePagination.page < resourcePagination.pageCount)

  const handleLoadMoreResources = () => {
    if (resourceLoadingMore || !resourceHasMore) return
    setResourcePage(prev => prev + 1)
  }

  // Fetch drivers with debounced search
  useEffect(() => {
    // Only fetch when showing the driver selection UI (search input visible)
    const isDriverSearchVisible = !selectedDriver && !driverFormVisible
    if (!isDriverSearchVisible) {
      // UI not visible, don't fetch
      return
    }
    
    const searchTerm = inlineDriverSearch.trim()
    
    // Don't fetch if no search and no existing data (prevents fetch on panel mount)
    // Only fetch when: user typed something OR we're refreshing existing data
    if (!searchTerm && searchedDrivers.length === 0) {
      return
    }
    
    // Clear results if search term is too short (but not empty)
    // Keep UI visible, just don't search
    if (searchTerm.length === 1) {
      setDriverSearchLoading(false)
      // Don't clear existing results, just stop fetching
      return
    }
    
    // Debounce the search
    const timer = setTimeout(() => {
      setDriverSearchLoading(true)
      const params: any = { status: "available", page: 1, pageSize }
      if (searchTerm) params.search = searchTerm
      
      driversApi.getDriversPaginated(params)
        .then(({ data, pagination }) => {
          setSearchedDrivers(Array.isArray(data) ? data : [])
          setDriverSearchPagination(pagination)
        })
        .catch(() => {
          setSearchedDrivers([])
          setDriverSearchPagination(null)
        })
        .finally(() => setDriverSearchLoading(false))
    }, searchTerm ? 500 : 0) // No delay for initial fetch, 500ms for search
    
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inlineDriverSearch, pageSize]) // Only re-run when search or pageSize changes (not selectedDriver)

  // Fetch vehicles with debounced search
  useEffect(() => {
    // Only fetch when showing the vehicle selection UI (search input visible)
    const isVehicleSearchVisible = !selectedVehicle && !vehicleFormVisible
    if (!isVehicleSearchVisible) {
      // UI not visible, don't fetch
      return
    }
    
    const searchTerm = inlineVehicleSearch.trim()
    
    // Don't fetch if no search and no existing data (prevents fetch on panel mount)
    // Only fetch when: user typed something OR we're refreshing existing data
    if (!searchTerm && searchedVehicles.length === 0) {
      return
    }
    
    // Clear results if search term is too short (but not empty)
    // Keep UI visible, just don't search
    if (searchTerm.length === 1) {
      setVehicleSearchLoading(false)
      // Don't clear existing results, just stop fetching
      return
    }
    
    // Debounce the search
    const timer = setTimeout(() => {
      setVehicleSearchLoading(true)
      const params: any = { status: "available", type: requiredVehicleType || vehicleType, page: 1, pageSize }
      if (searchTerm) params.search = searchTerm
      
      vehiclesApi.getVehiclesPaginated(params)
        .then(({ data, pagination }) => {
          setSearchedVehicles(Array.isArray(data) ? data : [])
          setVehicleSearchPagination(pagination)
        })
        .catch(() => {
          setSearchedVehicles([])
          setVehicleSearchPagination(null)
        })
        .finally(() => setVehicleSearchLoading(false))
    }, searchTerm ? 500 : 0) // No delay for initial fetch, 500ms for search
    
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inlineVehicleSearch, requiredVehicleType, vehicleType, pageSize]) // Only re-run when search or vehicle type changes (not selectedVehicle)

  const canLoadMoreDrivers = Boolean(driverSearchPagination && driverSearchPagination.page < driverSearchPagination.pageCount)
  const canLoadMoreVehicles = Boolean(vehicleSearchPagination && vehicleSearchPagination.page < vehicleSearchPagination.pageCount)

  // Trigger initial fetch when "Create New Resource" form opens
  useEffect(() => {
    if (!showResourceForm || isGuestMode) return  // Skip for guest mode
    
    // Fetch drivers if showing driver selection and no data yet
    const shouldFetchDrivers = !selectedDriver && !driverFormVisible && searchedDrivers.length === 0
    if (shouldFetchDrivers) {
      setDriverSearchLoading(true)
      driversApi.getDriversPaginated({ status: "available", page: 1, pageSize })
        .then(({ data, pagination }) => {
          setSearchedDrivers(Array.isArray(data) ? data : [])
          setDriverSearchPagination(pagination)
        })
        .catch(() => {
          setSearchedDrivers([])
          setDriverSearchPagination(null)
        })
        .finally(() => setDriverSearchLoading(false))
    }
    
    // Fetch vehicles if showing vehicle selection and no data yet
    const shouldFetchVehicles = !selectedVehicle && !vehicleFormVisible && searchedVehicles.length === 0
    if (shouldFetchVehicles) {
      setVehicleSearchLoading(true)
      vehiclesApi.getVehiclesPaginated({ 
        status: "available", 
        type: requiredVehicleType || vehicleType, 
        page: 1, 
        pageSize 
      })
        .then(({ data, pagination }) => {
          setSearchedVehicles(Array.isArray(data) ? data : [])
          setVehicleSearchPagination(pagination)
        })
        .catch(() => {
          setSearchedVehicles([])
          setVehicleSearchPagination(null)
        })
        .finally(() => setVehicleSearchLoading(false))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showResourceForm, requiredVehicleType, vehicleType, isGuestMode]) // Refetch when form opens or vehicle type changes

  const handleLoadMoreDrivers = async () => {
    if (!driverSearchPagination || driverSearchLoading) return
    if (driverSearchPagination.page >= driverSearchPagination.pageCount) return
    setDriverSearchLoading(true)
    try {
      const nextPage = (driverSearchPagination.page || 1) + 1
      const { data, pagination } = await driversApi.getDriversPaginated({
        status: "available",
        search: inlineDriverSearch.trim(),
        page: nextPage,
        pageSize,
      })
      setSearchedDrivers(prev => [...prev, ...(Array.isArray(data) ? data : [])])
      setDriverSearchPagination(pagination)
    } finally {
      setDriverSearchLoading(false)
    }
  }

  const handleLoadMoreVehicles = async () => {
    if (!vehicleSearchPagination || vehicleSearchLoading) return
    if (vehicleSearchPagination.page >= vehicleSearchPagination.pageCount) return
    setVehicleSearchLoading(true)
    try {
      const nextPage = (vehicleSearchPagination.page || 1) + 1
      const { data, pagination } = await vehiclesApi.getVehiclesPaginated({
        status: "available",
        search: inlineVehicleSearch.trim(),
        type: requiredVehicleType || vehicleType,
        page: nextPage,
        pageSize,
      })
      setSearchedVehicles(prev => [...prev, ...(Array.isArray(data) ? data : [])])
      setVehicleSearchPagination(pagination)
    } finally {
      setVehicleSearchLoading(false)
    }
  }

  const handleCreateDriverInline = async () => {
    if (!newDriverData.name?.trim()) {
      toast({ title: t('common.error', 'Error'), description: t('assets.driverValidation', 'Driver name, phone, and ID are required'), variant: 'destructive' })
      return
    }
    const driverIdResult = validateDriverId(newDriverData.driverId ?? '')
    if (!driverIdResult.valid) {
      toast({ title: t('common.error', 'Error'), description: t(driverIdResult.errorKey ?? 'validation.driverIdFormat'), variant: 'destructive' })
      return
    }
    const phoneResult = validateSaudiPhone(newDriverData.phone ?? '')
    if (!phoneResult.valid) {
      toast({ title: t('common.error', 'Error'), description: t(phoneResult.errorKey ?? 'validation.phoneSaudiFormat'), variant: 'destructive' })
      return
    }
    if (newDriverData.whatsappNumber?.trim()) {
      const waResult = validateSaudiPhone(newDriverData.whatsappNumber)
      if (!waResult.valid) {
        toast({ title: t('common.error', 'Error'), description: t(waResult.errorKey ?? 'validation.phoneSaudiFormat'), variant: 'destructive' })
        return
      }
    }

    const normalizedPhone = normalizeSaudiPhoneForStorage(newDriverData.phone ?? '') ?? newDriverData.phone
    const normalizedDriverId = normalizeDriverIdForStorage(newDriverData.driverId ?? '') ?? newDriverData.driverId
    const normalizedWhatsapp = newDriverData.whatsappNumber?.trim()
      ? (normalizeSaudiPhoneForStorage(newDriverData.whatsappNumber) ?? newDriverData.whatsappNumber)
      : ''

    setIsCreatingResource(true)
    try {
      if (isGuestMode) {
        // Guest mode: Create driver locally (no API call)
        const localDriver: Driver = {
          id: `local-driver-${Date.now()}`,
          documentId: `local-driver-${Date.now()}`,
          name: newDriverData.name,
          driverId: normalizedDriverId,
          phone: normalizedPhone,
          whatsappNumber: normalizedWhatsapp,
          licenseType: "light",
          licenseExpiry: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
        } as Driver

        setSelectedDriver(localDriver)
        setSearchedDrivers(prev => [localDriver, ...prev])
      } else {
        // Authenticated mode: Create via API
        const tenantId = tenantDocumentId || ''
        const createdDriver = await driversApi.createDriver({
          name: newDriverData.name,
          driverId: normalizedDriverId || undefined,
          phone: normalizedPhone || undefined,
          whatsappNumber: normalizedWhatsapp || undefined,
          preferredLanguage: newDriverData.preferredLanguage || 'ar',
          licenseType: "light",
          licenseExpiry: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split("T")[0],
          tenantId,
        } as any)
        setSelectedDriver(createdDriver)
        setSearchedDrivers(prev => [createdDriver, ...prev])
      }
      
      setDriverFormVisible(false)
      setNewDriverData({ name: "", phone: "", whatsappNumber: "", driverId: "", preferredLanguage: "ar" })
      setInlineDriverSearch("")
    } catch (error: any) {
      const message = error?.response?.data?.error?.message || error?.message || "Failed to create driver"
      toast({ title: t('common.error'), description: message, variant: "destructive" })
    } finally {
      setIsCreatingResource(false)
    }
  }

  const handleCreateVehicleInline = async () => {
    // Validation: License plate must be 1-4 digits + 1-3 letters
    const parts = newVehicleData.plateNumber.split('-')
    const digits = parts[0] || ''
    const letters = parts[1] || ''
    
    if (digits.length < 1 || digits.length > 4 || letters.length < 1 || letters.length > 3) {
      toast({ 
        title: t('common.error', 'Error'), 
        description: t('assets.plateValidation', 'License plate must be 1–4 digits and 1–3 letters'), 
        variant: "destructive" 
      })
      return
    }

    setIsCreatingResource(true)
    try {
      if (isGuestMode) {
        // Guest mode: Create vehicle locally (no API call)
        const now = new Date().toISOString()
        const resolvedVehicleType = requiredVehicleType || vehicleType || 'mixer'
        const localVehicle: Vehicle = {
          id: `local-vehicle-${Date.now()}`,
          documentId: `local-vehicle-${Date.now()}`,
          assetNumber: newVehicleData.plateNumber,
          name: {
            en: newVehicleData.plateNumber,
            ar: newVehicleData.plateNumber,
          },
          tenantId: 'local-tenant',
          category: 'VEHICLE',
          plateNumber: newVehicleData.plateNumber,
          type: resolvedVehicleType,
          make: '',
          model: '',
          capacity: {
            value: newVehicleData.capacity ? Number(newVehicleData.capacity) : 0,
            unit: 'tons',
          },
          status: 'available',
          ownership: 'owned',
          metadata: {
            vehicleType: resolvedVehicleType,
          },
          createdAt: now,
          updatedAt: now,
        }
        
        setSelectedVehicle(localVehicle)
        setSearchedVehicles(prev => [localVehicle, ...prev])
      } else {
        // Authenticated mode: Create via API
        const tenantId = tenantDocumentId || ''
        const createdVehicle = await vehiclesApi.createVehicle({
          plateNumber: newVehicleData.plateNumber,
          type: requiredVehicleType || vehicleType,
          capacity: newVehicleData.capacity ? { value: Number(newVehicleData.capacity), unit: "tons" } : undefined,
          tenantId,
        })
        setSelectedVehicle(createdVehicle)
        setSearchedVehicles(prev => [createdVehicle, ...prev])
      }
      
      setVehicleFormVisible(false)
      setNewVehicleData({ plateNumber: "", capacity: "" })
      setInlineVehicleSearch("")
    } catch (error: any) {
      const message = error?.response?.data?.error?.message || error?.message || "Failed to create vehicle"
      toast({ title: t('common.error'), description: message, variant: "destructive" })
    } finally {
      setIsCreatingResource(false)
    }
  }

  const handleSubmitResource = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedDriver || !selectedVehicle) return
    
    // Auto-generate resource name: Driver Name + License Plate
    const vehiclePlate = selectedVehicle.plateNumber || 'Vehicle'
    const autoGeneratedName = `${selectedDriver.name} - ${vehiclePlate}`
    
    setIsCreatingResource(true)
    try {
      if (isGuestMode) {
        // Guest mode: Create resource locally (no API call)
        const localResource = {
          id: `local-resource-${Date.now()}`,
          documentId: `local-resource-${Date.now()}`,
          name: autoGeneratedName,
          driver: selectedDriver,
          powerUnit: selectedVehicle,
          vehicle: selectedVehicle,
          driverName: selectedDriver.name,
          // Mark as local creation to be handled by backend
          _isLocalCreation: true,
        }
        
        const resourceOption = mapResourceToOption(localResource)
        if (resourceOption) {
          setResourceOptions(prev => [resourceOption, ...prev])
        }
        onSelectResource(localResource)
      } else {
        // Authenticated mode: Create via API
        const tenantId = tenantDocumentId || ''
        const createdResource = await resourcesApi.createResource({
          name: autoGeneratedName,
          driverId: (selectedDriver.documentId || selectedDriver.id) as any,
          vehicleId: selectedVehicle.documentId || selectedVehicle.id,
          tenantId,
        } as any)

        const richResource = {
          ...createdResource,
          name: autoGeneratedName,
          driver: selectedDriver,
          powerUnit: selectedVehicle,
        }

        setResourceOptions(prev => [mapResourceToOption(richResource)!, ...prev])
        onSelectResource(richResource)
      }
      
      setShowResourceForm(false)
      
      setShowResourceForm(false)
      setSelectedDriver(null)
      setSelectedVehicle(null)
    } catch (error: any) {
      const message = error?.response?.data?.error?.message || error?.message || "Failed to create resource"
      toast({ title: t('common.error'), description: message, variant: "destructive" })
    } finally {
      setIsCreatingResource(false)
    }
  }

  const handleSelectResource = (resource: ResourceOption) => {
    const isSelected =
      (selectedResourceDocumentId && resource.documentId && selectedResourceDocumentId === resource.documentId) ||
      (selectedResourceId && selectedResourceId === resource.id)
    
    if (isSelected && onClearSelection) {
      onClearSelection()
      return
    }
    
    onSelectResource(resource.raw)
  }

  return (
    <div className="mt-2 min-w-0 overflow-hidden rounded-xl border-2 border-primary/30 bg-gradient-to-br from-primary/5 to-primary/10 animate-in slide-in-from-top-2 duration-300">
      {/* Show resource list and search — always visible when not in create form */}
      {!showResourceForm && (
            <>
              {/* Hide search for guest mode - guests can't search existing resources */}
              {!isGuestMode && (
                <>
                  <div className="p-3 bg-white dark:bg-slate-900 border-b border-primary/20">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        value={resourceSearch}
                        onChange={(e) => setResourceSearch(e.target.value)}
                        placeholder={searchPlaceholder || t('assets.searchResources', 'Search resources, drivers, vehicles...')}
                        className="pl-9 h-9 text-sm"
                      />
                      {resourceSearch && (
                        <button
                          type="button"
                          onClick={() => setResourceSearch('')}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="sticky top-0 bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-700 z-10">
                    <button
                      type="button"
                      onClick={() => setShowResourceForm(true)}
                      className="w-full px-4 py-2.5 text-start hover:bg-primary/10 dark:hover:bg-primary/20 flex items-center gap-3 transition-colors"
                    >
                      <div className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                        <Plus className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1">
                        <p className="text-xs font-semibold text-primary">{t('assets.createNewResource', 'Create New Resource')}</p>
                        <p className="text-[10px] text-muted-foreground">{t('assets.createNewResourceHint', 'Select or create driver & vehicle')}</p>
                      </div>
                    </button>
                  </div>

                  {resourceOptions.length === 0 ? (
                    <div className="px-4 py-6 text-center">
                      <p className="text-xs text-muted-foreground">
                        {resourceLoading
                          ? t('common.loading', 'Loading...')
                          : t('assets.noResourcesFound', 'No resources found')}
                      </p>
                    </div>
                  ) : (
                    <div className="max-h-56 overflow-y-auto">
                      {resourceOptions.map((resource) => {
                        const isSelected =
                          (selectedResourceDocumentId && resource.documentId && selectedResourceDocumentId === resource.documentId) ||
                          (selectedResourceId && selectedResourceId === resource.id)

                        const secondaryParts = [
                          resource.driverName ? `${t('assets.driver', 'Driver')}: ${resource.driverName}` : '',
                          resource.powerUnitLabel ? `${t('assets.vehicle', 'Vehicle')}: ${resource.powerUnitLabel}` : '',
                          resource.trailerLabel ? `${t('assets.trailer', 'Trailer')}: ${resource.trailerLabel}` : '',
                        ].filter(Boolean)

                        return (
                          <button
                            key={resource.documentId ?? resource.id}
                            type="button"
                            className={`w-full px-4 py-3 text-start hover:bg-primary/10 dark:hover:bg-primary/20 flex items-start gap-3 border-b border-slate-100 dark:border-slate-800 last:border-0 transition-colors bg-white dark:bg-slate-900 ${isSelected ? 'bg-primary/10 dark:bg-primary/20' : ''}`}
                            onClick={() => handleSelectResource(resource)}
                          >
                            <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
                              <UserCheck className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-semibold text-foreground truncate">{resource.name}</p>
                              {secondaryParts.length > 0 && (
                                <p className="text-xs text-muted-foreground font-medium truncate">{secondaryParts.join(' • ')}</p>
                              )}
                            </div>
                            {isSelected && (
                              <Check className="w-4 h-4 text-primary flex-shrink-0" />
                            )}
                          </button>
                        )
                      })}
                      {resourceHasMore && (
                        <button
                          type="button"
                          onClick={handleLoadMoreResources}
                          disabled={resourceLoadingMore}
                          className="w-full py-2 text-center text-[10px] font-medium text-primary hover:bg-primary/5 rounded-lg transition-colors flex items-center justify-center gap-1"
                        >
                          {resourceLoadingMore ? <Loader2 className="w-3 h-3 animate-spin" /> : <ChevronDown className="w-3 h-3" />}
                          {t("common.loadMore", "Load more")}
                        </button>
                      )}
                    </div>
                  )}
                </>
              )}

              {isGuestMode && !showResourceForm && (
                <div className="p-4">
                  <Button
                    type="button"
                    onClick={() => setShowResourceForm(true)}
                    className="h-11 w-full rounded-xl"
                  >
                    {t("assets.createNewResource", "Create New Resource")}
                  </Button>
                </div>
              )}
            </>
          )}

          {showResourceForm && (
            <div className="space-y-6 p-4 sm:p-6">
              <div className="flex min-w-0 items-center justify-between gap-3">
                <h3 className="flex min-w-0 items-center gap-2 text-sm font-bold text-foreground">
                  <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                    <UserCheck className="w-4 h-4" />
                  </div>
                  <span className="truncate">{t("assets.createResource", "Create Resource")}</span>
                </h3>
                {/* Hide close button for guest mode - they should stay in create mode */}
                {!isGuestMode && (
                  <button
                    type="button"
                    onClick={() => setShowResourceForm(false)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-slate-200/50 dark:hover:bg-slate-700/50 transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              <form onSubmit={handleSubmitResource} className="space-y-5">
                <div className="space-y-3">
                  <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("assets.driver", "Driver")}</Label>

                  {selectedDriver ? (
                    <div className="relative group p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 flex items-center gap-3 transition-[box-shadow,border-color] hover:shadow-sm">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center flex-shrink-0 text-emerald-600 dark:text-emerald-400">
                        <UserIcon className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100 truncate">{selectedDriver.name}</p>
                        {selectedDriver.phone && <p className="text-xs text-emerald-700 dark:text-emerald-400 truncate">{selectedDriver.phone}</p>}
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedDriver(null)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-emerald-700 hover:bg-emerald-200/50 dark:hover:bg-emerald-900/50 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : !driverFormVisible ? (
                    <div className="space-y-2">
                      {/* Hide search for guest mode - guests can't search existing drivers */}
                      {!isGuestMode && (
                        <>
                          <div className="relative group">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                            <Input
                              value={inlineDriverSearch}
                              onChange={(e) => setInlineDriverSearch(e.target.value)}
                              placeholder={t("assets.searchOrCreateDriver", "Search or create driver...")}
                              className="pl-10 pr-10 h-11 bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 focus:bg-white dark:focus:bg-slate-900 transition-[border-color,box-shadow] rounded-xl"
                            />
                            {inlineDriverSearch && (
                              <button
                                type="button"
                                onClick={() => setInlineDriverSearch("")}
                                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-muted-foreground hover:text-foreground rounded-lg hover:bg-slate-200/50 dark:hover:bg-slate-700/50 transition-colors"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </>
                      )}

                      <button
                        type="button"
                        onClick={() => setDriverFormVisible(true)}
                        className="w-full h-11 px-4 border-2 border-dashed border-slate-200 dark:border-slate-700 hover:border-primary/50 hover:bg-primary/5 rounded-xl flex items-center justify-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-[background-color,border-color,color] group"
                      >
                        <div className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-800 group-hover:bg-primary/20 flex items-center justify-center transition-colors">
                          <Plus className="w-3.5 h-3.5" />
                        </div>
                        {t("assets.createNewDriver", "Create New Driver")}
                      </button>

                      {!isGuestMode && searchedDrivers.length > 0 && (
                        <div className="max-h-48 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900 shadow-sm divide-y divide-slate-100 dark:divide-slate-800">
                          {searchedDrivers.map(d => (
                            <button
                              key={d.id}
                              type="button"
                              onClick={() => setSelectedDriver(d)}
                              className="w-full p-3 text-start hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex items-center gap-3 group"
                            >
                              <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-muted-foreground group-hover:text-primary group-hover:bg-primary/10 transition-colors">
                                <UserIcon className="w-4 h-4" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-foreground">{d.name}</p>
                                {d.phone && <p className="text-xs text-muted-foreground">{d.phone}</p>}
                              </div>
                            </button>
                          ))}
                          {canLoadMoreDrivers && (
                            <button
                              type="button"
                              onClick={handleLoadMoreDrivers}
                              disabled={driverSearchLoading}
                              className="w-full py-2 text-center text-[10px] font-medium text-primary hover:bg-primary/5 rounded-lg transition-colors flex items-center justify-center gap-1"
                            >
                              {driverSearchLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ChevronDown className="w-3 h-3" />}
                              {t("common.loadMore", "Load more")}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3 animate-in slide-in-from-top-2 dark:bg-slate-800/50 dark:border-slate-700 sm:p-4">
                      <div className="grid gap-3">
                        <Input placeholder={t("assets.driverNamePlaceholder", "Name in English")} value={newDriverData.name} onChange={(e) => setNewDriverData(prev => ({ ...prev, name: e.target.value }))} className="h-10 bg-white dark:bg-slate-900" />
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <SaudiPhoneInput value={(newDriverData.phone ?? '').replace(/\D/g, '').slice(0, 10)} onChange={(digits) => setNewDriverData(prev => ({ ...prev, phone: digits }))} placeholder={t('assets.driverPhoneRequiredPlaceholder', 'Driver phone (required) e.g. 512345678')} className="h-10 bg-white dark:bg-slate-900 [&_input]:h-10" />
                          <SaudiPhoneInput value={(newDriverData.whatsappNumber ?? '').replace(/\D/g, '').slice(0, 10)} onChange={(digits) => setNewDriverData(prev => ({ ...prev, whatsappNumber: digits }))} placeholder={t('assets.whatsappOptionalPlaceholder', 'WhatsApp number (optional) e.g. 512345678')} className="h-10 bg-white dark:bg-slate-900 [&_input]:h-10" />
                        </div>
                        <Input placeholder={t("assets.driverId", "Driver ID (10 digits)")} inputMode="numeric" maxLength={10} value={newDriverData.driverId} onChange={(e) => setNewDriverData(prev => ({ ...prev, driverId: e.target.value.replace(/\D/g, '').slice(0, 10) }))} className="h-10 bg-white dark:bg-slate-900" />
                        <div className="space-y-2">
                          <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                            {t('fleet.preferredLanguage', 'Preferred Language')}
                          </Label>
                          <Select value={newDriverData.preferredLanguage} onValueChange={(val) => setNewDriverData(prev => ({ ...prev, preferredLanguage: val }))}>
                          <SelectTrigger className="h-10 bg-white dark:bg-slate-900">
                            <SelectValue placeholder={t('fleet.preferredLanguagePlaceholder', 'Preferred language')} />
                          </SelectTrigger>
                          <SelectContent>
                            {DRIVER_PREFERRED_LANGUAGES.map(lang => (
                              <SelectItem key={lang} value={lang}>
                                {lang === 'en' ? t('fleet.langEn', 'English') : lang === 'ar' ? t('fleet.langAr', 'Arabic') : t('fleet.langUr', 'Urdu')}
                              </SelectItem>
                            ))}
                          </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="grid gap-3 pt-1 sm:grid-cols-2">
                        <Button type="button" variant="outline" size="sm" className="h-10 w-full sm:h-9" onClick={() => setDriverFormVisible(false)}>{t("common.cancel", "Cancel")}</Button>
                        <Button 
                          type="button" 
                          size="sm" 
                          className="h-10 w-full sm:h-9" 
                          onClick={handleCreateDriverInline} 
                          disabled={!newDriverData.name || !newDriverData.phone || !newDriverData.driverId}
                        >
                          {t("common.create", "Create")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="h-px bg-slate-100 dark:bg-slate-800" />

                <div className="space-y-3">
                  <Label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">{t("assets.vehicle", "Vehicle")}</Label>

                  {selectedVehicle ? (
                    <div className="relative group p-3 rounded-xl bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 flex items-center gap-3 transition-[box-shadow,border-color] hover:shadow-sm">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center flex-shrink-0 text-emerald-600 dark:text-emerald-400">
                        <Truck className="w-5 h-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100 truncate">{selectedVehicle.plateNumber || selectedVehicle.plateNumber}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => setSelectedVehicle(null)}
                        className="w-8 h-8 flex items-center justify-center rounded-lg text-emerald-700 hover:bg-emerald-200/50 dark:hover:bg-emerald-900/50 transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ) : !vehicleFormVisible ? (
                    <div className="space-y-2">
                      {/* Hide search for guest mode - guests can't search existing vehicles */}
                      {!isGuestMode && (
                        <>
                          <div className="relative group">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground group-focus-within:text-primary transition-colors" />
                            <Input
                              value={inlineVehicleSearch}
                              onChange={(e) => setInlineVehicleSearch(e.target.value)}
                              placeholder={t("assets.searchOrCreateVehicle", "Search or create vehicle...")}
                              className="pl-10 pr-10 h-11 bg-slate-50 dark:bg-slate-800/50 border-slate-200 dark:border-slate-700 focus:bg-white dark:focus:bg-slate-900 transition-[border-color,box-shadow] rounded-xl"
                            />
                            {inlineVehicleSearch && (
                              <button
                                type="button"
                                onClick={() => setInlineVehicleSearch("")}
                                className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center text-muted-foreground hover:text-foreground rounded-lg hover:bg-slate-200/50 dark:hover:bg-slate-700/50 transition-colors"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        </>
                      )}

                      <button
                        type="button"
                        onClick={() => setVehicleFormVisible(true)}
                        className="w-full h-11 px-4 border-2 border-dashed border-slate-200 dark:border-slate-700 hover:border-primary/50 hover:bg-primary/5 rounded-xl flex items-center justify-center gap-2 text-sm font-medium text-muted-foreground hover:text-primary transition-[background-color,border-color,color] group"
                      >
                        <div className="w-6 h-6 rounded-full bg-slate-100 dark:bg-slate-800 group-hover:bg-primary/20 flex items-center justify-center transition-colors">
                          <Plus className="w-3.5 h-3.5" />
                        </div>
                        {t("assets.createNewVehicle", "Create New Vehicle")}
                      </button>

                      {!isGuestMode && searchedVehicles.length > 0 && (
                        <div className="max-h-48 overflow-y-auto border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900 shadow-sm divide-y divide-slate-100 dark:divide-slate-800">
                          {searchedVehicles.map(v => (
                            <button
                              key={v.id}
                              type="button"
                              onClick={() => setSelectedVehicle(v)}
                              className="w-full p-3 text-start hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex items-center gap-3 group"
                            >
                              <div className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-muted-foreground group-hover:text-primary group-hover:bg-primary/10 transition-colors">
                                <Truck className="w-4 h-4" />
                              </div>
                              <div>
                                <p className="text-sm font-medium text-foreground">{v.plateNumber || v.plateNumber}</p>
                              </div>
                            </button>
                          ))}
                          {canLoadMoreVehicles && (
                            <button
                              type="button"
                              onClick={handleLoadMoreVehicles}
                              disabled={vehicleSearchLoading}
                              className="w-full py-2 text-center text-[10px] font-medium text-primary hover:bg-primary/5 rounded-lg transition-colors flex items-center justify-center gap-1"
                            >
                              {vehicleSearchLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ChevronDown className="w-3 h-3" />}
                              {t("common.loadMore", "Load more")}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-3 animate-in slide-in-from-top-2 dark:bg-slate-800/50 dark:border-slate-700 sm:p-4">
                      <div className="space-y-2">
                        <Label className="text-[11px] font-semibold text-muted-foreground">{t("assets.vehicleType", "Vehicle Type")}</Label>
                        <Select value={requiredVehicleType || vehicleType} onValueChange={(value) => setVehicleType(value as VehicleType)} disabled={Boolean(requiredVehicleType)}>
                          <SelectTrigger className="h-10 bg-white dark:bg-slate-900">
                            <SelectValue placeholder={t("assets.vehicleType", "Vehicle Type")} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="mixer">{formatVehicleTypeLabel('mixer', t)}</SelectItem>
                            <SelectItem value="flatbed">{formatVehicleTypeLabel('flatbed', t)}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2">
                          <Input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]{1,4}"
                            value={plateParts.digits}
                            onChange={(e) => applyPlateDigitsChange(newVehicleData.plateNumber, e.target.value, (next) => setNewVehicleData(prev => ({ ...prev, plateNumber: next })), focusPlateInput)}
                            className="h-10 min-w-0 bg-white text-center font-mono tracking-widest dark:bg-slate-900"
                            placeholder="1234"
                            maxLength={LICENSE_PLATE_DIGITS_LENGTH}
                            ref={plateDigitsRef}
                          />
                          <span className="text-slate-400 font-semibold">-</span>
                          <Input
                            type="text"
                            inputMode="text"
                            pattern="[A-Z]{1,3}"
                            value={plateParts.letters}
                            onChange={(e) => applyPlateLettersChange(newVehicleData.plateNumber, e.target.value, (next) => setNewVehicleData(prev => ({ ...prev, plateNumber: next })))}
                            onKeyDown={(e) => applyPlateLettersKeyDown(plateParts.letters, e.key, focusPlateInput)}
                            className="h-10 min-w-0 bg-white text-center font-mono tracking-widest dark:bg-slate-900"
                            placeholder={t('fleet.plateLettersPlaceholder', 'ABC')}
                            maxLength={LICENSE_PLATE_LETTERS_LENGTH}
                            ref={plateLettersRef}
                          />
                        </div>
                        <Input type="number" placeholder={t("assets.capacity", "Capacity (tons)")} value={newVehicleData.capacity} onChange={(e) => setNewVehicleData(prev => ({ ...prev, capacity: e.target.value }))} className="h-10 min-w-0 bg-white dark:bg-slate-900" />
                      </div>
                      <div className="grid gap-3 pt-1 sm:grid-cols-2">
                        <Button type="button" variant="outline" size="sm" className="h-10 w-full sm:h-9" onClick={() => setVehicleFormVisible(false)}>{t("common.cancel", "Cancel")}</Button>
                        <Button 
                          type="button" 
                          size="sm" 
                          className="h-10 w-full sm:h-9" 
                          onClick={handleCreateVehicleInline} 
                          disabled={(() => {
                            const parts = newVehicleData.plateNumber.split('-')
                            const digits = parts[0] || ''
                            const letters = parts[1] || ''
                            return digits.length < 1 || digits.length > 4 || letters.length < 1 || letters.length > 3
                          })()}
                        >
                          {t("common.create", "Create")}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>

                <div className="grid gap-3 pt-2 sm:grid-cols-2">
                  <Button type="button" variant="outline" size="lg" className="h-11 w-full rounded-xl" onClick={() => setShowResourceForm(false)}>{t("common.cancel", "Cancel")}</Button>
                  <Button
                    type="submit"
                    size="lg"
                    className="h-11 w-full rounded-xl shadow-lg shadow-primary/20"
                    disabled={isCreatingResource || !selectedDriver || !selectedVehicle}
                  >
                    {isCreatingResource ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        {t("common.creating", "Creating...")}
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        {t("common.create", "Create")}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          )}
    </div>
  )
}
